import { z } from 'zod';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';

import { zClientOAuthConfig, zClientPostBody } from '../../../api/generated/pap/zod.gen';
import { getInitialFormState } from '../../../utils/form';
import {
  useUpdateCustomApplication,
  useAddCustomApplication,
} from './customApplications.mutations';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';
import { CustomApplicationPostBody } from '../types';

enum CustomApplicationFormKeys {
  Name = 'name',
  Description = 'description',
  HomepageUrl = 'homepageURL',
  IconURL = 'iconURL',
  AppPolicy = 'appPolicy',
  AuthNPolicy = 'authnPolicy',
  OAuthConfig = 'OAuthConfig',
  ClientId = 'clientId',
  ClientSecret = 'clientSecret',
}

const customApplicationFormSchema = zClientPostBody.extend({
  iconURL: z.string().url().optional(),
});

type CustomApplicationFormData = z.infer<typeof customApplicationFormSchema>;

// factory to create a runtime schema with translated messages
const createCustomApplicationFormSchema = (
  t: (key: string, opts?: Record<string, string>) => string
) => {
  const nameSchema = (zClientPostBody.shape.name as z.ZodString).nonempty({
    message: t('customApplications.nameRequired', { ns: 'settings' }),
  });

  const descriptionSchema = (zClientPostBody.shape.description as z.ZodString).nonempty({
    message: t('customApplications.descriptionRequired', { ns: 'settings' }),
  });

  const iconURLSchema = z
    .string()
    .url({ message: t('customApplications.iconUrlInvalid', { ns: 'settings' }) })
    .optional();

  return zClientPostBody.extend({
    name: nameSchema,
    description: descriptionSchema,
    iconURL: iconURLSchema,
    appPolicy: zClientPostBody.shape.appPolicy.nonempty({
      message: t('customApplications.appPolicyRequired', { ns: 'settings' }),
    }),
    clientSecret: z.string(),
    OAuthConfig: z
      .preprocess((raw) => {
        if (typeof raw === 'string') {
          const s = raw.trim();
          if (!s) return undefined;
          try {
            return JSON.parse(s);
          } catch {
            // return original raw so the subsequent validation fails with a message
            return raw;
          }
        }
        // if it's already an object or undefined, pass it through
        return raw;
      }, z.unknown())
      .refine(
        (val) => {
          if (val === undefined) return false;
          const result = zClientOAuthConfig.safeParse(val);
          return result.success;
        },
        {
          message: t('customApplications.oauthConfigInvalid', { ns: 'settings' }),
        }
      ),
  });
};

const useCustomApplicationForm = (
  id: string | undefined,
  defaultValues?: Partial<CustomApplicationFormData>
) => {
  const FORM_NAME = 'CustomApplicationForm';

  const { t } = useTranslation('settings');
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();

  // build a runtime schema with i18n messages
  const tCustomApplicationFormSchema = createCustomApplicationFormSchema(t);

  type RuntimeForm = z.infer<typeof tCustomApplicationFormSchema>;

  const [formFields, setFormFields] = useState<RuntimeForm>(
    getInitialFormState(
      tCustomApplicationFormSchema,
      defaultValues as unknown as Partial<RuntimeForm>
    )
  );
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const { mutateAsync: updateCustomApplication, isPending: isUpdating } =
    useUpdateCustomApplication();
  const { mutateAsync: addCustomApplication, isPending: isAdding } = useAddCustomApplication();

  const isPending = isUpdating || isAdding;
  const isEdit = !!id;

  // All notification texts
  const notificationTexts: string[] = [
    t('customApplications.addFailure'),
    t('customApplications.addFailureDesc', { name: formFields.name }),
    t('customApplications.editFailure'),
    t('customApplications.editFailureDesc', { name: formFields.name }),
  ];

  const getStringMax = (schema?: z.ZodTypeAny): number | undefined => {
    const s = schema as z.ZodString | undefined;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checks = (s as any)?._def?.checks as Array<any> | undefined;
    const maxCheck = checks?.find((c) => c.kind === 'max');
    return maxCheck?.value as number | undefined;
  };

  const descriptionMax = getStringMax(tCustomApplicationFormSchema.shape.description);

  const validateForm = (fields: RuntimeForm) => {
    const result = tCustomApplicationFormSchema.safeParse(fields);
    if (!result.success) {
      const { fieldErrors, formErrors } = result.error.flatten();
      const fe = fieldErrors as Record<string, string[]>;
      setErrors(fe);
      return { success: false, fieldErrors: fe, formErrors };
    }
    setErrors({});
    return { success: true, data: result.data };
  };

  const clearFields = () => {
    setFormFields(
      getInitialFormState(
        tCustomApplicationFormSchema,
        (defaultValues as unknown as Partial<RuntimeForm>) ?? {}
      )
    );
    setErrors({});
  };

  const clearErrors = () => {
    setErrors({});
  };

  const showFailureNotification = () => {
    const operation = isEdit ? 'edit' : 'add';
    const header = t(`customApplications.${operation}Failure`);
    const desc = t(`customApplications.${operation}FailureDesc`, { name: formFields.name });
    if (!notificationTexts.includes(header)) return;
    notificationService.addNotification(header, desc, Statuses.error);
  };

  const handleFormSubmit: () => Promise<boolean> = async () => {
    // run the runtime schema so preprocess/transform has already run
    const parseResult = tCustomApplicationFormSchema.safeParse(formFields);

    if (!parseResult.success) {
      const { fieldErrors } = parseResult.error.flatten();
      setErrors(fieldErrors as Record<string, string[]>);
      return false;
    }

    // parsedData contains values after preprocess/transform (OAuthConfig will be an object)
    const parsedData = parseResult.data as unknown as CustomApplicationPostBody;

    try {
      // clear any existing OAuthConfig error
      setErrors((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { OAuthConfig: _o, ...rest } = prev;
        return rest;
      });

      if (id) await updateCustomApplication({ id, payload: parsedData });
      else await addCustomApplication(parsedData);

      return true;
    } catch (error: unknown) {
      console.error('Operation failed', error);
      if (!showError(error)) {
        showFailureNotification();
      }
      return false;
    }
  };

  return {
    FORM_NAME,
    formFields,
    errors,
    isPending,
    descriptionMax,
    setFormFields,
    setErrors,
    validateForm,
    clearFields,
    clearErrors,
    handleFormSubmit,
  };
};
export {
  useCustomApplicationForm,
  customApplicationFormSchema,
  type CustomApplicationFormData,
  CustomApplicationFormKeys,
};
